import { Heart, ImageOff, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useGalleryImage } from "./useGalleryImage";

const TAG_DEFAULT = "bg-surface2 text-ink-soft";

// Share (Stage 3, 2026-11) — `?tab=gallery&item=<id>` on the existing /build/me route (see
// FreelancerPortal.jsx's `deepLink` handling and Gallery.jsx's matching read-on-mount effect).
// No new route. M2 (Cursor fix pass): no public app origin is configured anywhere in this
// project's env files (checked frontend/.env and backend/.env — neither has one, and this
// fix pass isn't inventing a new one), so this still builds the link off
// window.location.origin, same as before.
function _shareUrl(itemId) {
  return `${window.location.origin}/build/me?tab=gallery&item=${encodeURIComponent(itemId)}`; // L5
}

// M1 (Cursor fix pass, 2026-11) — navigator.clipboard is unavailable over plain HTTP, in
// older/embedded webviews, and some browsers block it outside a direct user gesture even when
// present; `writeText` can also simply reject (permission denied). A hidden textarea +
// document.execCommand("copy") is the standard, still-supported fallback for exactly that
// gap — "Couldn't copy the link" is only shown if THAT also fails, never before trying it.
function _copyViaHiddenTextarea(text) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  document.body.removeChild(textarea);
  return ok;
}

export async function shareGalleryItem(itemId) {
  const url = _shareUrl(itemId);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
      return;
    } catch {
      // fall through to the hidden-textarea fallback below
    }
  }
  if (_copyViaHiddenTextarea(url)) {
    toast.success("Link copied");
  } else {
    toast.error("Couldn't copy the link");
  }
}

/** BTS tile's own image — authenticated blob fetch via useGalleryImage (see that file's
 * header comment for why a bare <img src> can't work here), a neutral pulse placeholder
 * while loading, a quiet fallback icon (never a broken-image glyph) on error. */
function BtsImage({ item }) {
  const { objectUrl, status } = useGalleryImage(item.image_url);
  if (status === "error") {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-surface2" data-testid="gallery-card-bts-image-fallback">
        <ImageOff className="h-6 w-6 text-faint" />
      </div>
    );
  }
  if (!objectUrl) {
    return <div className="absolute inset-0 bg-surface2 animate-pulse" data-testid="gallery-card-bts-image-loading" />;
  }
  return <img src={objectUrl} alt="" data-testid="gallery-card-bts-image-img" className="absolute inset-0 h-full w-full object-cover" />;
}

function CreatorRow({ creator }) {
  if (!creator) return null;
  const initials = (creator.name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const sub = [creator.role, creator.city].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center gap-2.5">
      <Link to={`/member/${creator.freelancer_id}`} data-testid="gallery-card-creator-avatar"
        className="h-7 w-7 shrink-0 rounded-full bg-ink text-brand font-bold text-[10px] flex items-center justify-center">
        {initials}
      </Link>
      <div className="min-w-0 flex-1">
        <Link to={`/member/${creator.freelancer_id}`} data-testid="gallery-card-creator-name"
          className="text-[13px] font-semibold text-ink border-b border-ink/20 hover:border-ink/50 transition-colors">
          {creator.name}
        </Link>
        {sub && <div className="mt-0.5 text-[11.5px] text-faint truncate">{sub}</div>}
      </div>
    </div>
  );
}

function CardFooter({ item, onToggleLike, likePending }) {
  return (
    <div className="px-3.5 pt-3 pb-3">
      <CreatorRow creator={item.creator} />
      <div className="mt-2.5 flex items-center gap-1.5">
        {(item.tags || []).map((t) => (
          <span key={t} data-testid={`gallery-card-tag-${t.toLowerCase()}`}
            className={`rounded-[10px] px-2 py-1 text-[9.5px] font-semibold uppercase tracking-wider ${TAG_DEFAULT}`}>
            {t}
          </span>
        ))}
        <div className="flex-1" />
        <button type="button" onClick={() => shareGalleryItem(item.id)} aria-label="Share"
          data-testid={`gallery-card-share-button-${item.id}`}
          className="flex h-7 w-7 items-center justify-center rounded-full text-faint hover:text-ink hover:bg-surface2 transition-colors">
          <Share2 className="h-[14px] w-[14px]" />
        </button>
        <button type="button" onClick={() => onToggleLike(item)} disabled={likePending} aria-label={item.liked_by_me ? "Unlike" : "Like"}
          data-testid={`gallery-card-like-button-${item.id}`}
          className={`flex items-center gap-1.5 text-[13px] font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${item.liked_by_me ? "text-red-500" : "text-faint hover:text-ink"}`}>
          <Heart className="h-[15px] w-[15px]" fill={item.liked_by_me ? "currentColor" : "none"} data-testid={`gallery-card-like-icon-${item.id}`} />
          <span data-testid={`gallery-card-like-count-${item.id}`}>{item.like_count || 0}</span>
        </button>
      </div>
    </div>
  );
}

/** The Gallery, Stage 2+3 follow-up (2026-11) — the heart is now a real toggle (optimistic
 * update + rollback owned by the parent, see Gallery.jsx::toggleLike) and a Share button
 * copies a stable deep link. Both video and BTS open the SAME centred lightbox on click
 * (GalleryLightbox) — there is no more in-card play-in-place iframe swap; the thumbnail
 * always stays a thumbnail, exactly one media window can ever be open at a time across the
 * whole feed. Tag-chip/search FILTERING and lightbox prev/next arrows remain out of scope
 * (G3, not built here). */
export function GalleryCard({ item, onOpenLightbox, onToggleLike, likePending }) {
  return (
    <div className="break-inside-avoid mb-4 rounded-xl border border-hairline bg-paper overflow-hidden" data-testid={`gallery-card-${item.id}`}>
      {item.type === "video" ? (
        <button type="button" onClick={() => onOpenLightbox(item)} data-testid="gallery-card-video-frame"
          className="relative block w-full bg-ink-deep/10 group" style={{ aspectRatio: "16 / 9" }} aria-label="Open video">
          <img src={item.thumbnail_url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="h-12 w-12 rounded-full bg-ink/70 text-white flex items-center justify-center group-hover:bg-ink/85 transition-colors">
              <svg viewBox="0 0 24 24" className="h-4 w-4 ml-0.5" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </span>
          </span>
        </button>
      ) : (
        <button type="button" onClick={() => onOpenLightbox(item)} data-testid="gallery-card-bts-image"
          className="relative block w-full bg-ink-deep/10" style={{ aspectRatio: item.width && item.height ? `${item.width} / ${item.height}` : "4 / 5" }}>
          <BtsImage item={item} />
          <span className="absolute top-2.5 left-2.5 rounded bg-ink text-white text-[9.5px] font-bold uppercase tracking-[0.16em] px-2 py-1">BTS</span>
        </button>
      )}
      <CardFooter item={item} onToggleLike={onToggleLike} likePending={likePending} />
    </div>
  );
}
